import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('aalim_token'));
    const [loading, setLoading] = useState(true);

    const axiosInstance = axios.create({
        baseURL: API,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('aalim_token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('aalim_token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (name, email, phone, password) => {
        const response = await axios.post(`${API}/auth/register`, {
            name,
            email,
            phone,
            password
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('aalim_token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('aalim_token');
        setToken(null);
        setUser(null);
    };

    const isPremium = user?.subscription_status && 
        ['ustadh_monthly', 'ustadh_yearly', 'active'].includes(user.subscription_status);
    const isAdmin = user?.is_admin === true;

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        isPremium,
        isAdmin,
        axiosInstance,
        refreshUser: fetchUser
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
